import React from "react";
import { createUseStyles, useTheme } from "react-jss";
import Img from "gatsby-image";

import github from 'images/github-logo.svg';
import twitter from 'images/twitter-logo.svg';
import linkedin from 'images/linkedin-logo.svg';

import { formatPublishDate, prependApiUrl } from 'helpers';
import Tag from 'components/Tag';
import { PageInner } from 'components/PageContainers';
import { FixedAspectRatioContainer, FixedAspectRatioElement } from 'components/FixedAspectRatioElement';
import { StrapiImageFluid } from "../../components/StrapiImage";

const useStyles = createUseStyles({
  blogHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    maxWidth: '50rem',
    margin: '3rem auto 5rem'
  },
  authorSocialMedia: {
    opacity: '.5',
    '&:hover': {
      opacity: '1'
    },
    '& + &': {
      marginLeft: '1.5rem'
    }
  },
  authorSocialMediaContainer: {
    display: 'flex',
    margin: '2.5rem auto 0'
  },
  title: {
    fontWeight: 600,
    fontSize: '3.1rem'
  },
  author: {
    fontWeight: 600,
    marginTop: '2rem',
  },
  date: {
    fontSize: '1.4rem'
  },
  tag: {
    fontWeight: 600,
    maxWidth: '100%'
  },
  banner: {
    width: '100%',
    marginBottom: '5rem'
  },
  microdata: {
    display: 'none'
  }
});

const socialMedias = {
  twitter: {
    icon: twitter,
    name: 'Twitter'
  },
  facebook: {
    icon: github,
    name: 'Facebook'
  },
  linkedin: {
    icon: linkedin,
    name: 'LinkedIn'
  }
}

const BlogHeaderBanner = ({ banner_youtube_id, banner_image }) => {
  const theme = useTheme();
  const styles = useStyles({ theme });

  if (banner_youtube_id) {
    return (
      <FixedAspectRatioContainer className={styles.banner} ratio="56.25%">
        <FixedAspectRatioElement
          el="iframe"
          width="100%"
          height="100%"
          src={`https://www.youtube.com/embed/${banner_youtube_id}`}
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen
          itemProp="video"
        />
      </FixedAspectRatioContainer>
    );
  } else if (banner_image) {
    return (
      <FixedAspectRatioContainer className={styles.banner} ratio={`${banner_image.height/banner_image.width*100}%`}>
        <FixedAspectRatioElement
          el={StrapiImageFluid}
          image={banner_image}
          itemProp="image"
        />
      </FixedAspectRatioContainer>
    );
  } else {
    return null;
  }
};

const BlogHeader = ({ blogPost }) => {
  const theme = useTheme();
  const styles = useStyles({ theme });
  const { title, blog_tags, author, published_at, banner_youtube_id, banner_image, abstract } = blogPost;

  return (
    <PageInner>
      <div className={styles.blogHeader}>
        <Tag className={styles.tag}>Blog Post</Tag>
        <h1 className={styles.title} itemProp="headline">{title}</h1>
        {blog_tags && <Tag className={styles.tag}>{blog_tags.map(tag => tag.name).join(', ')}</Tag>}
        <span itemProp="description abstract" className={styles.microdata}>{abstract}</span>
        <p itemProp="author publisher" itemScope itemType="http://schema.org/Person" className={styles.author}>
            <span itemProp="name">{author}</span>
        </p>
        {published_at && (
          <span className={styles.date} itemProp="dateModified datePublished">{formatPublishDate(published_at)}</span>
        )}
        {author && author.socialMedias && (
          <div className={styles.authorSocialMediaContainer}>
            {author.socialMedias && Object.keys(author.socialMedias).map(siteKey => (
              <a className={styles.authorSocialMedia} href={author.socialMedias[siteKey]}>
                <img src={socialMedias[siteKey].icon} alt={socialMedias[siteKey].name} />
              </a>
            ))}
          </div>
        )}
      </div>
      <BlogHeaderBanner banner_image={banner_image} banner_youtube_id={banner_youtube_id} />
    </PageInner>
  )
};

export default BlogHeader;
